import { createApi, createBaseQuery } from '@adv-frontend/modules-query';
import { apiV1Request } from '#shared/api/requests';
import {
    type OpenApiDeleteMoyskladIntegrationSettingsRequest,
    type OpenApiDeleteMoyskladIntegrationSettingsResponseSuccess,
    type OpenApiGetMoyskladAttributeIntegrationSettingsRequest,
    type OpenApiGetMoyskladAttributeIntegrationSettingsResponseSuccess,
    type OpenApiGetMoyskladCategoriesRequest,
    type OpenApiGetMoyskladCategoriesResponseSuccess,
    type OpenApiGetMoyskladDictionariesResponseSuccess,
    type OpenApiGetMoyskladDictionariesSearchParams,
    type OpenApiGetMoyskladIntegrationSettingsRequest,
    type OpenApiGetMoyskladIntegrationSettingsResponseSuccess,
    type OpenApiGetMoyskladOrderIntegrationSettingsRequest,
    type OpenApiGetMoyskladOrderIntegrationSettingsResponseSuccess,
    type OpenApiGetMoyskladPriceIntegrationSettingsRequest,
    type OpenApiGetMoyskladPriceIntegrationSettingsResponseSuccess,
    type OpenApiSchemaMoySkladCommissionerContractSettingsRequest,
    type OpenApiSetupMoyskladAttributeIntegrationRequest,
    type OpenApiSetupMoyskladAttributeIntegrationResponseSuccess,
    type OpenApiSetupMoyskladComissionerContractIntegrationResponseSuccess,
    type OpenApiSetupMoyskladIntegrationRequest,
    type OpenApiSetupMoyskladIntegrationResponseSuccess,
    type OpenApiSetupMoyskladOrderIntegrationRequest,
    type OpenApiSetupMoyskladOrderIntegrationResponseSuccess,
    type OpenApiSetupMoyskladPriceIntegrationRequest,
    type OpenApiSetupMoyskladPriceIntegrationResponseSuccess,
} from '#shared/api/schema';
import { OpenApiSetupMoyskladComissionerContractIntegrationUrl } from '#shared/api/schema';

const getSearchQueryParams = ({
    limit = 0,
    offset = 0,
    search = '',
}: {
    limit: number;
    offset: number;
    search: string;
}) => {
    const params = new URLSearchParams({
        limit: String(limit),
        offset: String(offset),
        search,
    });

    return params;
};

const MoySkladOrdersSetupTag = 'MoySkladOrdersSetup' as const;

const api = createApi({
    name: 'entities/integration/api/moySkladApi',
    baseQuery: createBaseQuery(apiV1Request),
    tagTypes: [MoySkladOrdersSetupTag],
    endpoints: (builder) => {
        return {
            setupAdd: builder.mutation<
                OpenApiSetupMoyskladIntegrationResponseSuccess,
                OpenApiSetupMoyskladIntegrationRequest
            >({
                query: (payload) => {
                    return {
                        url: '/integrations/moysklad/setup',
                        method: 'POST',
                        body: payload,
                    };
                },
            }),
            setupDelete: builder.mutation<
                OpenApiDeleteMoyskladIntegrationSettingsResponseSuccess,
                OpenApiDeleteMoyskladIntegrationSettingsRequest
            >({
                query: () => {
                    return {
                        url: '/integrations/moysklad/setup',
                        method: 'DELETE',
                    };
                },
            }),
            setupGet: builder.query<
                OpenApiGetMoyskladIntegrationSettingsResponseSuccess,
                OpenApiGetMoyskladIntegrationSettingsRequest
            >({
                query: () => {
                    return {
                        url: '/integrations/moysklad/setup',
                        method: 'GET',
                    };
                },
            }),
            dictionaryOrganizations: builder.query<
                OpenApiGetMoyskladDictionariesResponseSuccess,
                OpenApiGetMoyskladDictionariesSearchParams
            >({
                query: ({ limit = 0, offset = 0, search = '' }) => {
                    const params = getSearchQueryParams({ limit, offset, search });

                    return {
                        url: `/integrations/moysklad/dictionaries/organizations?${params.toString()}`,
                        method: 'GET',
                    };
                },
            }),
            dictionaryCounterparties: builder.query<
                OpenApiGetMoyskladDictionariesResponseSuccess,
                OpenApiGetMoyskladDictionariesSearchParams
            >({
                query: ({ limit = 0, offset = 0, search = '' }) => {
                    const params = getSearchQueryParams({ limit, offset, search });

                    return {
                        url: `/integrations/moysklad/dictionaries/counterparties?${params.toString()}`,
                        method: 'GET',
                    };
                },
            }),
            dictionaryServices: builder.query<
                OpenApiGetMoyskladDictionariesResponseSuccess,
                OpenApiGetMoyskladDictionariesSearchParams
            >({
                query: ({ limit = 0, offset = 0, search = '' }) => {
                    const params = getSearchQueryParams({ limit, offset, search });

                    return {
                        url: `/integrations/moysklad/dictionaries/services?${params.toString()}`,
                        method: 'GET',
                    };
                },
            }),
            dictionaryStatuses: builder.query<
                OpenApiGetMoyskladDictionariesResponseSuccess,
                OpenApiGetMoyskladDictionariesSearchParams
            >({
                query: () => {
                    return {
                        url: '/integrations/moysklad/dictionaries/statuses',
                        method: 'GET',
                    };
                },
            }),
            dictionaryPriceTypes: builder.query<
                OpenApiGetMoyskladDictionariesResponseSuccess,
                OpenApiGetMoyskladDictionariesSearchParams
            >({
                query: () => {
                    return {
                        url: '/integrations/moysklad/dictionaries/pricetypes',
                        method: 'GET',
                    };
                },
            }),
            dictionaryCategories: builder.query<
                OpenApiGetMoyskladCategoriesResponseSuccess,
                OpenApiGetMoyskladCategoriesRequest
            >({
                query: () => {
                    return {
                        url: '/integrations/moysklad/dictionaries/categories',
                        method: 'GET',
                    };
                },
            }),
            dictionaryAttributes: builder.query<
                OpenApiGetMoyskladDictionariesResponseSuccess,
                OpenApiGetMoyskladDictionariesSearchParams
            >({
                query: () => {
                    return {
                        url: '/integrations/moysklad/dictionaries/attributes',
                        method: 'GET',
                    };
                },
            }),
            dictionaryCommissionerContracts: builder.query<
                OpenApiGetMoyskladDictionariesResponseSuccess,
                OpenApiGetMoyskladDictionariesSearchParams
            >({
                query: ({ limit = 0, offset = 0, search = '' }: { limit: number; offset: number; search: string }) => {
                    const params = getSearchQueryParams({ limit, offset, search });

                    return {
                        url: `/integrations/moysklad/dictionaries/commissioner_contracts?${params.toString()}`,
                        method: 'GET',
                    };
                },
            }),
            commissionerContractsSetup: builder.mutation<
                OpenApiSetupMoyskladComissionerContractIntegrationResponseSuccess,
                OpenApiSchemaMoySkladCommissionerContractSettingsRequest
            >({
                query: (payload) => {
                    return {
                        url: OpenApiSetupMoyskladComissionerContractIntegrationUrl,
                        method: 'POST',
                        body: payload,
                    };
                },
            }),
            pricesSetup: builder.query<
                OpenApiGetMoyskladPriceIntegrationSettingsResponseSuccess,
                OpenApiGetMoyskladPriceIntegrationSettingsRequest
            >({
                query: () => {
                    return {
                        url: '/integrations/moysklad/setup/prices',
                        method: 'GET',
                    };
                },
            }),
            pricesSetupUpdate: builder.mutation<
                OpenApiSetupMoyskladPriceIntegrationResponseSuccess,
                OpenApiSetupMoyskladPriceIntegrationRequest
            >({
                query: (payload) => {
                    return {
                        url: '/integrations/moysklad/setup/prices',
                        method: 'POST',
                        body: payload,
                    };
                },
            }),
            ordersSetup: builder.mutation<
                OpenApiSetupMoyskladOrderIntegrationResponseSuccess,
                OpenApiSetupMoyskladOrderIntegrationRequest
            >({
                query: (payload) => {
                    return {
                        url: '/integrations/moysklad/setup/orders',
                        method: 'POST',
                        body: payload,
                    };
                },
            }),
            ordersSetupGet: builder.query<
                OpenApiGetMoyskladOrderIntegrationSettingsResponseSuccess,
                OpenApiGetMoyskladOrderIntegrationSettingsRequest
            >({
                query: () => {
                    return {
                        url: '/integrations/moysklad/setup/orders',
                        method: 'GET',
                    };
                },
                providesTags: [MoySkladOrdersSetupTag],
            }),
            attributeSetupGet: builder.query<
                OpenApiGetMoyskladAttributeIntegrationSettingsResponseSuccess,
                OpenApiGetMoyskladAttributeIntegrationSettingsRequest
            >({
                query: () => {
                    return {
                        url: '/integrations/moysklad/setup/attributes',
                        method: 'GET',
                    };
                },
            }),
            attributeSetupUpdate: builder.mutation<
                OpenApiSetupMoyskladAttributeIntegrationResponseSuccess,
                OpenApiSetupMoyskladAttributeIntegrationRequest
            >({
                query: (payload) => {
                    return {
                        url: '/integrations/moysklad/setup/attributes',
                        method: 'POST',
                        body: payload,
                    };
                },
            }),
        };
    },
});

export const integrationMoySkladSetupAdd = api.endpoints.setupAdd.mutate;
export const integrationMoySkladSetupDelete = api.endpoints.setupDelete.mutate;
export const integrationMoySkladSetupGet = api.endpoints.setupGet.query;
export const useIntegrationMoySkladSetup = api.useSetupGetQuery;

export const integrationMoySkladDictionaryOrganizations = api.endpoints.dictionaryOrganizations;
export const integrationMoySkladDictionaryCounterparties = api.endpoints.dictionaryCounterparties;
export const integrationMoySkladDictionaryServices = api.endpoints.dictionaryServices;
export const useIntegrationMoySkladDictionaryStatuses = api.useDictionaryStatusesQuery;
export const integrationMoySkladDictionaryCommissionerContracts = api.endpoints.dictionaryCommissionerContracts;
export const useIntegrationMoySkladDictionaryPriceTypes = api.useDictionaryPriceTypesQuery;
export const useIntegrationMoySkladDictionaryCategories = api.useDictionaryCategoriesQuery;

export const integrationMoySkladOrdersSetup = api.endpoints.ordersSetup.mutate;
export const useIntegrationMoySkladOrdersSetup = api.useOrdersSetupGetQuery;

export const integrationMoySkladOrdersSetupUpdateResetCache = () => {
    api.util.invalidateTags([MoySkladOrdersSetupTag]);
};

export const integrationMoySkladCommissionerContractsSetup = api.endpoints.commissionerContractsSetup.mutate;

export const useIntegrationMoySkladPricesSetup = api.usePricesSetupQuery;
export const integrationMoySkladPricesSetupUpdate = api.endpoints.pricesSetupUpdate.mutate;

export const useIntegrationMoySkladDictionaryAttributes = () => api.useDictionaryAttributesQuery({});
export const useIntegrationMoySkladGetAttributeSetup = api.useAttributeSetupGetQuery;
export const integrationMoySkladAttributeSetupUpdate = api.endpoints.attributeSetupUpdate.mutate;
